/** Landing page text (navbar, hero, sections, footer). */
export default {
  logo: {
    tagline: "AI Creative Studio",
  },
  hero: {
    bgAlt: "Abstract holographic waves background",
    chipOutput: "Full HD Video",
    chipEngine: "Neural Engine",
    chipRender: "AI Render",
    badge: "Next-generation AI image & video studio",
    title: "AI-Powered <holo>Image & Video</holo> Creation for Professionals",
    subtitle:
      "Generate stunning visuals instantly with next-generation artificial intelligence. Cinematic quality, zero learning curve.",
    viewPricing: "View Pricing",
    stats: {
      output: { value: "1080p", label: "Max video output" },
      models: { value: "1 point", label: "Per image" },
      render: { value: "2 points", label: "Per video clip" },
      uptime: { value: "10", label: "Free points to start" },
    },
  },
  howItWorks: {
    eyebrow: "How it works",
    title: "From idea to cinematic visual in three steps",
    subtitle: "No design skills needed. Our pipeline does the heavy lifting so you can focus on creating.",
    steps: {
      upload: {
        title: "Describe",
        description:
          "Write a short prompt describing the image or video you want — the subject, style, lighting and mood.",
      },
      generate: {
        title: "Generate",
        description:
          "Our AI renders your image in seconds, or animates it into a short cinematic video clip. An image costs 1 point, a video clip 2 points.",
      },
      download: {
        title: "Download",
        description:
          "Download images up to 1024 × 1024 px and video clips up to Full HD (1080p), with full commercial usage rights.",
      },
    },
  },
  demo: {
    eyebrow: "AI demo",
    title: "Watch a rough sketch become a masterpiece",
    subtitle: "Drag the slider to compare. This is the same engine powering your next project.",
    sketch: "Sketch",
    aiGenerated: "AI Generated",
    altGenerated: "{{subject}} — generated",
    altSketch: "{{subject}} — sketch",
    perfumeBottle: "Perfume bottle",
    portrait: "Portrait",
    videoAlt: "AI video generation preview",
    playVideo: "Play video preview",
    videoBadge: "Video clip — Full HD",
    generateTitle: "Generate your own image",
    generateSubtitle: "Describe anything and our engine will render it in seconds.",
    promptPlaceholder: "A cinematic portrait of a fox under neon city lights…",
    generate: "Generate",
    generating: "Generating…",
    rendering: "Rendering your image…",
    resultAlt: "AI generated result",
    noImage: "No image was returned. Please try again.",
    genericError: "Something went wrong while generating.",
  },
  pricing: {
    eyebrow: "Pricing",
    title: "Simple plans, cinematic results",
    subtitle: "Start with 10 free points. Buy a points pack when you need more — a one-time payment, not a subscription.",
    mostPopular: "Most popular",
    footnote: "Secure card checkout · One-time payment, no subscription · 30-day money-back guarantee",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Frequently asked questions",
    items: {
      howItWorks: {
        question: "How does AI generation work?",
        answer:
          "You write a text prompt. Our AI models interpret it and generate a brand-new image from scratch — no stock library, no templates. For a video, the generated image is animated into a short cinematic clip. You can refine your prompt and generate again until the result matches your vision.",
      },
      formats: {
        question: "What formats do you support?",
        answer:
          "Images download as JPEG or PNG at up to 1024 × 1024 px. Video clips download as MP4 at up to Full HD (1080p). All plans get the same output quality, watermark-free, with full commercial usage rights.",
      },
      payments: {
        question: "How do payments work?",
        answer:
          "Each plan is a one-time payment for a pack of points — it is not a subscription and nothing renews automatically. You pay securely by card on the hosted payment page of DSK Bank or PostBank, with any major credit or debit card. Prices are in euros (EUR) and include all applicable taxes, with no hidden fees and no minimum order amount.",
      },
      freePoints: {
        question: "What do the 10 free points include?",
        answer:
          "Every new account gets 10 free points once, when you sign up — enough for 10 images or 5 video clips. You don't enter a card, it isn't a trial that turns into a paid plan, and nothing is charged afterwards. You only pay if you choose to buy a points pack, and every pack is a one-time payment.",
      },
      security: {
        question: "Is my data secure?",
        answer:
          "Yes. Everything you send us travels over encrypted HTTPS connections, passwords are stored only as one-way hashes, and we never see or store your full card details — you enter them on the bank's PCI DSS-compliant payment page, protected by 3-D Secure. We do not use your prompts or results to train AI models, and you keep full ownership of everything you generate. See our Privacy Policy for details.",
      },
      refunds: {
        question: "What is your refund policy?",
        answer:
          "The first payment of a Starter, Pro or Elite plan has a 30-day money-back guarantee. If no points from that purchase have been used, we refund it in full. If some have been used, we refund the unused points. A Custom plan is refundable within 14 days on the same basis. Plans are one-time purchases, so you are never charged again unless you place a new order.",
      },
    },
  },
  contact: {
    eyebrow: "Contact",
    title: "Let's build something cinematic",
    subtitle:
      "Have a question about our plans, need help with a project, or want a custom workflow for your team? Drop us a line — we reply within one business day.",
    emailUs: "Email us",
    supportEmail: "Support",
    registeredBusiness: "Registered business",
    companyName: "MEDIA STYLE 25 EOOD (МЕДИА СТИЛ 25 ЕООД)",
    uic: "UIC: 208512111 · Razgrad, Bulgaria",
    registeredAddress: "Registered address",
    address: "7 Aprilsko Vastanie Blvd, Entrance B, Floor 1, Apartment 34, 7200 Razgrad, Bulgaria",
    callUs: "Call us",
    customerService: "Customer service",
    workingHours: "Working hours",
    hours: "Monday – Friday, 09:00 – 18:00 (Bulgarian time)",
    responseTime: "Response time",
    within24h: "Within one business day",
    name: "Name",
    namePlaceholder: "Your name",
    email: "Email",
    subject: "Subject",
    subjectPlaceholder: "What's this about?",
    message: "Message",
    messagePlaceholder: "Tell us what you need…",
    messageHint: "Up to 500 characters.",
    sending: "Sending…",
    send: "Send message",
    success: "Thanks! Your message has been sent. We'll be in touch shortly.",
    networkError: "Network error. Please check your connection and try again.",
  },
  footer: {
    about:
      "MEDIA-STYLE is the trading name of MEDIA STYLE 25 EOOD, a Bulgarian creative-tech company building AI tools for image and video generation.",
    company: "Company",
    howItWorks: "How it works",
    legal: "Legal",
    contact: "Customer service",
    registered: "MEDIA STYLE 25 EOOD (МЕДИА СТИЛ 25 ЕООД) · UIC 208512111 · Registered office: Razgrad, Bulgaria · Country of domicile: Bulgaria (EU)",
    copyright: "© {{year}} MEDIA STYLE 25 EOOD (trading as MEDIA-STYLE). All rights reserved.",
    builtIn: "Proudly built in Bulgaria",
    securePayments: "Secure payments with",
  },
};
